if (!customElements.get('promo-wheel')) {
  customElements.define(
    'promo-wheel',
    class extends HTMLElement {
      constructor() {
        super();
        this.spinning = false;
        this.hasPlayed = false;
        this.currentRotation = 0;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.segments = JSON.parse(this.querySelector('[data-segments]').textContent);
        this.config = JSON.parse(this.querySelector('[data-config]').textContent);
        this.storageKey = `promo-wheel-played-${this.sectionId}`;

        this.canvas = this.querySelector('.promo-wheel__canvas');
        this.ctx = this.canvas.getContext('2d');
        this.spinBtn = this.querySelector('.promo-wheel__spin-btn');
        this.resetBtn = this.querySelector('.promo-wheel__reset');
        this.popup = this.querySelector('.promo-wheel__popup');
        this.popupTitle = this.querySelector('.promo-wheel__popup-title');
        this.popupMessage = this.querySelector('.promo-wheel__popup-message');
        this.popupCodeWrap = this.querySelector('.promo-wheel__popup-code');
        this.popupCodeText = this.querySelector('.promo-wheel__popup-code-text');
        this.popupCopyBtn = this.querySelector('.promo-wheel__popup-copy-btn');
        this.popupClose = this.querySelector('.promo-wheel__popup-close');
        this.resultCard = this.querySelector('.promo-wheel__result-card');
        this.resultLabel = this.querySelector('.promo-wheel__result-label');
        this.resultMessage = this.querySelector('.promo-wheel__result-message');
        this.resultCodeRow = this.querySelector('.promo-wheel__result-code-row');
        this.resultCodeText = this.querySelector('.promo-wheel__result-code-text');
        this.resultCopyBtn = this.querySelector('.promo-wheel__result-copy-btn');

        this.setupCanvas();
        this.drawWheel(0);
        this.bindEvents();
        this.checkAlreadyPlayed();
        this.handleResize();
      }

      disconnectedCallback() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
        }
      }

      handleResize() {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.spinning) {
            this.setupCanvas();
            this.drawWheel(this.currentRotation);
          }
        });
        const wrapper = this.querySelector('.promo-wheel__wheel-wrapper');
        this.resizeObserver.observe(wrapper);
      }

      getTodayKey() {
        return new Date().toISOString().slice(0, 10);
      }

      setupCanvas() {
        const wrapper = this.querySelector('.promo-wheel__wheel-wrapper');
        const wrapperSize = wrapper.offsetWidth;
        const isMobile = window.innerWidth <= 749;
        const wheelSizeSetting = isMobile
          ? (this.config.wheelSizeMobile || this.config.wheelSize || 85)
          : (this.config.wheelSize || 85);
        const wheelSizePercent = wheelSizeSetting / 100;
        const size = wrapperSize * wheelSizePercent;
        const dpr = window.devicePixelRatio || 1;
        this.canvas.width = size * dpr;
        this.canvas.height = size * dpr;
        this.canvas.style.width = size + 'px';
        this.canvas.style.height = size + 'px';
        this.ctx.scale(dpr, dpr);
        this.wheelSize = size;
        this.centerX = size / 2;
        this.centerY = size / 2;
        this.radius = size / 2 - 8;
      }

      drawWheel(rotation) {
        const { ctx, centerX, centerY, radius, segments } = this;
        const total = segments.length;
        const arc = (2 * Math.PI) / total;

        ctx.clearRect(0, 0, this.wheelSize, this.wheelSize);

        // Shadow under wheel
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.15)';
        ctx.shadowBlur = 20;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.restore();

        segments.forEach((seg, i) => {
          const startAngle = rotation + i * arc - Math.PI / 2;
          const endAngle = startAngle + arc;

          // Segment fill
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.arc(centerX, centerY, radius, startAngle, endAngle);
          ctx.closePath();
          ctx.fillStyle = seg.bg;
          ctx.fill();

          // Segment border
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 5;
          ctx.stroke();

          // Text
          ctx.save();
          ctx.translate(centerX, centerY);
          ctx.rotate(startAngle + arc / 2);

          ctx.fillStyle = seg.color;

          // Adjust font size for mobile
          const isMobile = window.innerWidth <= 749;
          const baseFontSize = Math.max(11, Math.min(16, radius / 10));
          const fontSize = isMobile ? Math.max(9, baseFontSize * 0.75) : baseFontSize;

          ctx.font = `bold ${fontSize}px sans-serif`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';

          const label = seg.label;
          const maxWidth = radius - 30;
          let displayText = label;
          while (ctx.measureText(displayText).width > maxWidth && displayText.length > 1) {
            displayText = displayText.slice(0, -1);
          }
          if (displayText !== label) displayText += '…';

          ctx.fillText(displayText, radius - 15, 0);
          ctx.restore();
        });

        // Center circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius * 0.12, 0, 2 * Math.PI);
        ctx.fillStyle = this.config.pointerColor || '#111';
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      bindEvents() {
        this.spinBtn.addEventListener('click', () => this.spin());

        if (this.popupClose) {
          this.popupClose.addEventListener('click', () => this.hidePopup());
        }
        if (this.popup) {
          this.popup.addEventListener('click', (e) => {
            if (e.target === this.popup) this.hidePopup();
          });
          this.popup.addEventListener('keyup', (e) => {
            if (e.code === 'Escape') this.hidePopup();
          });
        }

        if (this.popupCopyBtn) {
          this.popupCopyBtn.addEventListener('click', () => {
            this.copyCode(this.popupCodeText.textContent, this.popupCopyBtn);
          });
        }
        if (this.resultCopyBtn) {
          this.resultCopyBtn.addEventListener('click', () => {
            this.copyCode(this.resultCodeText.textContent, this.resultCopyBtn);
          });
        }
        if (this.resetBtn) {
          this.resetBtn.addEventListener('click', () => this.reset());
        }
      }

      checkAlreadyPlayed() {
        const savedDate = localStorage.getItem(this.storageKey + '-date');
        if (savedDate && savedDate === this.getTodayKey()) {
          this.hasPlayed = true;
          this.spinBtn.disabled = true;
          this.spinBtn.innerHTML = `<span class="promo-wheel__spin-btn-text">${this.config.alreadyPlayedMessage || 'Vous avez déjà joué'}</span>`;

          // Restore result card
          const savedResult = localStorage.getItem(this.storageKey + '-result');
          if (savedResult && this.resultCard) {
            const savedCode = localStorage.getItem(this.storageKey + '-code');
            this.showResultCard(savedCode ? 'Votre code promo' : 'Résultat', savedResult, savedCode || null);
          }
        } else if (savedDate) {
          // New day — clear old data
          localStorage.removeItem(this.storageKey + '-date');
          localStorage.removeItem(this.storageKey + '-code');
          localStorage.removeItem(this.storageKey + '-result');
        }
      }

      showResultCard(label, message, code) {
        if (!this.resultCard) return;
        this.resultLabel.textContent = label;
        this.resultMessage.textContent = message;
        if (code) {
          this.resultCodeText.textContent = code;
          this.resultCodeRow.removeAttribute('hidden');
        } else {
          this.resultCodeRow.setAttribute('hidden', '');
        }
        this.resultCard.removeAttribute('hidden');
      }

      spin() {
        if (this.spinning || this.hasPlayed) return;
        this.spinning = true;
        this.spinBtn.disabled = true;
        this.spinBtn.classList.add('spinning');

        const total = this.segments.length;
        const arc = (2 * Math.PI) / total;

        // Pick random winning segment
        const winIndex = Math.floor(Math.random() * total);

        const targetSegmentAngle = winIndex * arc + arc / 2;
        const fullRotations = (3 + Math.floor(Math.random() * 3)) * 2 * Math.PI;
        const targetRotation = fullRotations + (2 * Math.PI - targetSegmentAngle);

        const startRotation = this.currentRotation;
        const totalRotation = targetRotation;
        const duration = 4000 + Math.random() * 1000;
        const startTime = performance.now();

        const animate = (now) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Cubic ease-out for natural deceleration
          const eased = 1 - Math.pow(1 - progress, 3);
          const currentAngle = startRotation + totalRotation * eased;

          this.drawWheel(currentAngle);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            this.currentRotation = currentAngle;
            this.spinning = false;
            this.spinBtn.classList.remove('spinning');
            this.hasPlayed = true;
            localStorage.setItem(this.storageKey + '-date', this.getTodayKey());
            this.onSpinComplete(this.segments[winIndex]);
          }
        };

        requestAnimationFrame(animate);
      }

      onSpinComplete(segment) {
        this.spinBtn.innerHTML = `<span class="promo-wheel__spin-btn-text">${this.config.alreadyPlayedMessage || 'Vous avez déjà joué'}</span>`;

        const hasCode = segment.code && segment.code.trim() !== '';

        // Show popup
        if (hasCode) {
          const msg = this.config.popupMessageWin || 'Vous avez gagné un code promo !';
          this.popupTitle.textContent = this.config.popupTitleWin || 'Bravo !';
          this.popupMessage.textContent = msg;
          this.popupCodeText.textContent = segment.code;
          this.popupCodeWrap.removeAttribute('hidden');

          this.showResultCard('Votre code promo', msg, segment.code);
          localStorage.setItem(this.storageKey + '-code', segment.code);
          localStorage.setItem(this.storageKey + '-result', msg);
        } else {
          const msg = this.config.popupMessageLose || 'Pas de chance cette fois-ci.';
          this.popupTitle.textContent = this.config.popupTitleLose || 'Dommage !';
          this.popupMessage.textContent = msg;
          this.popupCodeWrap.setAttribute('hidden', '');

          this.showResultCard('Résultat', msg, null);
          localStorage.setItem(this.storageKey + '-result', msg);
        }

        // Open popup with slight delay for dramatic effect
        setTimeout(() => this.showPopup(), 400);
      }

      showPopup() {
        this.popup.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        this.popup.querySelector('.promo-wheel__popup-content').focus();
      }

      hidePopup() {
        this.popup.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
      }

      copyCode(code, button) {
        navigator.clipboard.writeText(code).then(() => {
          const original = button.textContent;
          button.textContent = 'Copié !';
          button.classList.add('promo-wheel__copied');
          setTimeout(() => {
            button.textContent = original;
            button.classList.remove('promo-wheel__copied');
          }, 2000);
        });
      }

      reset() {
        localStorage.removeItem(this.storageKey + '-date');
        localStorage.removeItem(this.storageKey + '-code');
        localStorage.removeItem(this.storageKey + '-result');
        this.hasPlayed = false;
        this.spinning = false;
        this.currentRotation = 0;
        this.spinBtn.disabled = false;
        this.spinBtn.innerHTML = `<span class="promo-wheel__spin-btn-text">${this.config.spinButtonLabel || 'Tourner la roue'}</span>`;
        if (this.resultCard) this.resultCard.setAttribute('hidden', '');
        this.drawWheel(0);
      }
    }
  );
}
